import { RowData } from "@tanstack/react-table";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import { useContext } from "react";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  draggingBetweenRidesGridIdSelector,
  DragTypeEnum,
  DropTypeEnum,
  generateDropIdGridItem,
  IRideDto,
  IRideTableDto,
  IRydeRow,
  isRowExpandedSF,
  RenderSubComponentModel,
  ridesDraggingDataAtom,
} from "@ryde/shared";

import { DroppableRideWrapper } from "../../common";
import { TableWrapperContext } from "../generic/table/table-wrapper.context";

interface IGridDetailsPanel<T extends RowData> {
  row: IRydeRow<T>;
  renderSubComponent?: RenderSubComponentModel<T>;
}

export const GridTableDetailsPanel = <T extends RowData>({
  row,
  renderSubComponent,
}: IGridDetailsPanel<T>): JSX.Element => {
  const ride = row.original as IRideDto;
  const rideId = ride.id;
  const { entity } = usePageContext();
  const { isRidesEditOrAddRoute } = useContext(TableWrapperContext);

  const draggingGridRideId = useRecoilValue(draggingBetweenRidesGridIdSelector);
  const dragStatus = useRecoilValue(ridesDraggingDataAtom);

  const isRowExpanded = useRecoilValue(isRowExpandedSF([entity, rideId]));

  // Fully hide details panel when dragging a ride, not when dragging a passenger
  const isDraggingAnyRide = Boolean(
    draggingGridRideId && dragStatus?.type === DragTypeEnum.GRID_RIDE,
  );

  // Only visually hide details panel when dragging a passenger (but keep it accessible for drag)
  const isDraggingPassenger = Boolean(
    draggingGridRideId && dragStatus?.type === DragTypeEnum.GRID_PASSENGER,
  );

  return (
    <>
      {isRowExpanded && (
        <DroppableRideWrapper
          id={generateDropIdGridItem(rideId) + "_details_" + row.id}
          key={`${row.id}_${rideId}_details`}
          disabled={isRidesEditOrAddRoute || isDraggingAnyRide}
          element="tr"
          useMaxSize={false}
          dropData={{
            accepts: [DragTypeEnum.GRID_PASSENGER, DragTypeEnum.GRID_RIDE],
            typeDrop: DropTypeEnum.GRID_RIDE_DROP,
            rideDrop: ride as IRideTableDto,
          }}
          customClassName={clsx({
            details_panel_row_hidden_during_drag: isDraggingAnyRide,
            details_panel_row_hidden_during_passenger_drag: isDraggingPassenger,
          })}
        >
          <td
            data-e2e="table-expanded-cell"
            className={clsx("details_panel", {
              details_panel_cell_hidden_during_drag: isDraggingAnyRide,
            })}
            colSpan={row.getVisibleCells().length}
          >
            {!isDraggingAnyRide && (
              <AnimatePresence>{renderSubComponent?.({ row })}</AnimatePresence>
            )}
          </td>
        </DroppableRideWrapper>
      )}
    </>
  );
};
